// frontend/src/components/Layout.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
// Import Link component from react-router-dom for client-side navigation
import { Link } from 'react-router-dom';
// Import necessary icons from react-icons
import {
    FaDiscord, FaLandmark, FaMoneyBillWave, FaBars, FaTimes, FaHome, FaInfoCircle,
    FaUserShield, FaAngleDown, FaAngleRight, FaClipboardList, FaStore, FaHistory, FaUserCircle
} from 'react-icons/fa';

// --- Configuration ---
const DEFAULT_REFRESH_INTERVAL_MINUTES = 5;
const refreshIntervalMinutes = process.env.REACT_APP_ECONOMY_REFRESH_MINUTES
    ? parseInt(process.env.REACT_APP_ECONOMY_REFRESH_MINUTES, 10)
    : DEFAULT_REFRESH_INTERVAL_MINUTES;
const refreshIntervalMs = !isNaN(refreshIntervalMinutes) && refreshIntervalMinutes > 0
    ? refreshIntervalMinutes * 60 * 1000
    : 0;

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;
if (!BACKEND_API_URL) {
    console.error("CRITICAL CONFIG ERROR: REACT_APP_BACKEND_API_URL environment variable is not set.");
}

/**
 * A reusable layout component providing a consistent header, footer, background,
 * and a collapsible sidebar menu with conditional admin section.
 * Includes header graphic and icons.
 * Fetches and displays user economy data in the header when logged in.
 * Periodically refreshes the economy data based on environment settings.
 *
 * @param {object} props - Component props.
 * @param {object} [props.user] - The authenticated user object (optional). If provided, enables sidebar and shows role/logout.
 * @param {React.ReactNode} props.children - The content to render within the layout.
 * @returns {React.ReactElement} The rendered layout component.
 */
function Layout({ user, children }) {
  // --- Constants ---
  const discordInviteLink = "https://discord.gg/exodusdayz";
  const discordLoginLink = BACKEND_API_URL ? `${BACKEND_API_URL}/auth/discord` : '#';
  const discordLogoutLink = BACKEND_API_URL ? `${BACKEND_API_URL}/auth/logout` : '#';
  const economyApiUrl = BACKEND_API_URL ? `${BACKEND_API_URL}/api/economy` : null;
  const currentYear = new Date().getFullYear();

  // --- State ---
  const [economy, setEconomy] = useState({ bank: null, cash: null, total: null });
  const [loadingEconomy, setLoadingEconomy] = useState(false);
  const [economyError, setEconomyError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAdminSubmenuOpen, setIsAdminSubmenuOpen] = useState(false);

  // --- Refs ---
  const intervalIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  // --- Callbacks ---
  /** Toggles the visibility state of the main sidebar. */
  const toggleSidebar = () => { if (user) setIsSidebarOpen(!isSidebarOpen); };
  /** Toggles the visibility state of the admin submenu within the sidebar. */
  const toggleAdminSubmenu = () => { setIsAdminSubmenuOpen(!isAdminSubmenuOpen); };
  /** Closes the sidebar, e.g., after a navigation link is clicked. */
  const closeSidebar = () => { setIsSidebarOpen(false); };


  // --- Fetch Economy Data Function ---
  /** Fetches economy data from the backend API. */
  const fetchEconomy = useCallback(async () => {
    if (isFetchingRef.current || !economyApiUrl) {
        if (!economyApiUrl) console.error("[Layout] Cannot fetch economy: Backend API URL is not configured.");
        return;
    }
    isFetchingRef.current = true;
    setLoadingEconomy(true);
    setEconomyError(null);
    console.log('[Layout] Fetching economy data...');
    try {
      const response = await fetch(economyApiUrl, { credentials: 'include' });
      if (!response.ok) {
         const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
         throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      if (data.success && data.economy) {
        console.log('[Layout] Economy data received:', data.economy);
        setEconomy(data.economy);
      } else {
        throw new Error(data.message || 'Economy data not found in response.');
      }
    } catch (error) {
      console.error('[Layout] Error fetching economy data:', error);
      setEconomyError(error.message);
      setEconomy({ bank: null, cash: null, total: null });
    } finally {
      setLoadingEconomy(false);
      isFetchingRef.current = false;
    }
   }, [economyApiUrl]);

  // --- Effect for Initial Fetch and Interval Setup ---
  /** Handles fetching economy data on login and setting up the refresh interval. */
  useEffect(() => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    intervalIdRef.current = null;
    if (!user) {
        setEconomy({ bank: null, cash: null, total: null });
        setEconomyError(null);
        setLoadingEconomy(false);
        isFetchingRef.current = false;
        setIsSidebarOpen(false);
        setIsAdminSubmenuOpen(false);
        return;
    }
    if (economyApiUrl) { fetchEconomy(); }
    else { setEconomyError("Backend URL not configured."); }
    if (refreshIntervalMs > 0 && economyApiUrl) {
        intervalIdRef.current = setInterval(fetchEconomy, refreshIntervalMs);
    }
    return () => { if (intervalIdRef.current) clearInterval(intervalIdRef.current); };
   }, [user, fetchEconomy, economyApiUrl, refreshIntervalMs, refreshIntervalMinutes]);

  // --- Helper Functions ---
  /** Formats currency numbers for display. */
  const formatCurrency = (amount) => {
      if (economyError) return 'N/A';
      if (loadingEconomy && amount === null) return '...';
      if (amount === null || isNaN(amount)) return 'N/A';
      return new Intl.NumberFormat('en-US').format(amount);
  };

  return (
    // Outermost container
    <div className="relative h-screen bg-black text-white overflow-hidden flex flex-col">

        {/* Sidebar Overlay */}
        {user && isSidebarOpen && ( <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={toggleSidebar} aria-hidden="true"></div> )}

        {/* Sidebar */}
        {user && (
            <aside className={`fixed top-0 left-0 bottom-0 z-50 w-64 bg-gray-900 p-4 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out overflow-y-auto`}>
                {/* Sidebar Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">Menu</h2>
                    <button onClick={toggleSidebar} className="text-gray-400 hover:text-white" aria-label="Close menu"><FaTimes size={20} /></button>
                </div>

                {/* Sidebar Navigation */}
                <nav>
                    <ul>
                        {/* Standard Links - Use <Link> for internal navigation */}
                        <li className="mb-2">
                            <Link to="/dashboard" onClick={closeSidebar} className="flex items-center p-2 text-gray-300 hover:bg-gray-700 rounded-md">
                                <FaHome className="mr-3 flex-shrink-0" /> Dashboard
                            </Link>
                        </li>
                        <li className="mb-2">
                            {/* External link uses standard <a> tag */}
                            <a href={discordInviteLink} target="_blank" rel="noopener noreferrer" className="flex items-center p-2 text-gray-300 hover:bg-gray-700 rounded-md">
                                <FaDiscord className="mr-3 flex-shrink-0" /> Join Discord
                            </a>
                        </li>
                        <li className="mb-2">
                            {/* Placeholder button for About link */}
                            <button onClick={() => { alert('About page not yet implemented!'); closeSidebar(); }} className="w-full flex items-center p-2 text-gray-300 hover:bg-gray-700 rounded-md text-left">
                                <FaInfoCircle className="mr-3 flex-shrink-0" /> About
                            </button>
                        </li>

                        {/* Conditional Admin Section */}
                        {user.role === 'Founder' && (
                            <li className="mb-2">
                                {/* Admin Toggle Button */}
                                <button onClick={toggleAdminSubmenu} className="w-full flex items-center justify-between p-2 text-gray-300 hover:bg-gray-700 rounded-md text-left">
                                    <span className="flex items-center"><FaUserShield className="mr-3 flex-shrink-0" /> Admin</span>
                                    {isAdminSubmenuOpen ? <FaAngleDown /> : <FaAngleRight />}
                                </button>

                                {/* Admin Submenu */}
                                {isAdminSubmenuOpen && (
                                    <ul className="mt-1 pl-6 border-l border-gray-700 ml-2">
                                        <li className="mb-1">
                                            {/* Link to Boost History page */}
                                            <Link to="/admin/boost-history" onClick={closeSidebar} className="flex items-center p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md text-sm">
                                                <FaHistory className="mr-2 flex-shrink-0" /> Boost History
                                            </Link>
                                        </li>                                        
                                        <li className="mb-1 mt-1">
                                            {/* Use Link for internal admin routes */}
                                            <Link to="/admin/logs" onClick={closeSidebar} className="flex items-center p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md text-sm">
                                                <FaClipboardList className="mr-2 flex-shrink-0" /> Logs
                                            </Link>
                                        </li>
                                        <li className="mb-1">
                                            {/* Use Link for internal admin routes */}
                                            <Link to="/admin/shop" onClick={closeSidebar} className="flex items-center p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md text-sm">
                                                <FaStore className="mr-2 flex-shrink-0" /> Shop Items
                                            </Link>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        )}
                        {/* End Admin Section */}
                    </ul>
                </nav>
            </aside>
        )}

        {/* Main Content Wrapper */}
        <div className="flex flex-col flex-grow h-full">
            {/* Header Section */}
            <header className="relative z-30 p-4 flex items-center justify-between flex-shrink-0">
                {/* Left side content */}
                <div className="flex items-center space-x-4">
                    {user && (<button onClick={toggleSidebar} className="text-white p-1 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500 rounded" aria-label="Open menu"><FaBars size={24} /></button>)}
                    {/* External link uses <a> */}
                    <a href={discordInviteLink} target="_blank" rel="noopener noreferrer" title="Join Discord Server"><FaDiscord className="text-white text-3xl hover:text-gray-400 transition-colors" /></a>       
                    {/* Economy Info (visible only when logged in) */}
                    {user && (
                        <div className="flex items-center"> {/* Wrapper to group separator and economy info */}
                            {/* Separator: Hidden on extra-small, visible from sm screens up, with right margin */}
                            <span className="border-l border-gray-600 h-6 hidden sm:block mr-3"></span>
                            {/* Economy Info Block:
                                - Stacks vertically by default (extra-small screens).
                                - Becomes a horizontal row from 'sm' breakpoint upwards.
                                - Adjusts item alignment and spacing for each layout.
                                - Uses smaller text on extra-small screens.
                            */}
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3 text-xs sm:text-sm" title={economyError ? `Economy Error: ${economyError}` : ''}>
                                {/* Bank */}
                                <span className="flex items-center" title="Bank Balance"><FaLandmark className={`mr-1.5 flex-shrink-0 ${economyError ? 'text-red-500' : 'text-green-400'}`} /> {formatCurrency(economy.bank)}</span>
                                {/* Cash */}
                                <span className="flex items-center" title="Cash on Hand"><FaMoneyBillWave className={`mr-1.5 flex-shrink-0 ${economyError ? 'text-red-500' : 'text-green-400'}`} /> {formatCurrency(economy.cash)}</span>
                                {/* Total */}
                                <span className={`font-semibold sm:pl-2 ${economyError ? 'text-red-500' : ''}`} title="Total Balance">Total: {formatCurrency(economy.total)}</span>
                            </div>
                        </div>
                    )}
                    {/* End Economy Info */}
                </div>
                {/* Right side content */}
                <div className="flex items-center space-x-4">
                    {user ? ( /* Username, Role, Logout */
                        <>
                            <span className="text-sm text-gray-300 hidden sm:flex items-center">
                                <FaUserCircle className="mr-2 text-blue-300" />
                                {user.username}{user.discriminator && user.discriminator !== "0" ? `#${user.discriminator}` : ''}
                            </span>
                            <span className="text-sm font-semibold text-blue-300 hidden sm:inline">
                                Role: {user.role || 'N/A'}
                            </span>
                            <a href={discordLogoutLink}> <button className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-5 rounded-md transition-colors text-sm"> Logout </button> </a>
                        </>
                    ) : ( /* Login */ <a href={discordLoginLink}> <button className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition-colors text-sm"> Login </button> </a>
                    )}
                </div>
            </header>

            {/* Top Decorative Line */}
            <div className="relative z-20 border-b border-gray-700 mx-4 flex-shrink-0"></div>
            {/* Main Content Area */}
            <main className="relative z-10 flex-grow overflow-y-auto px-4 py-8">{children}</main>
            {/* Bottom Decorative Line */}
            <div className="relative z-20 border-t border-gray-700 mx-4 flex-shrink-0"></div>
            {/* Footer Section */}
            <footer className="relative z-20 text-center text-xs text-gray-500 p-4 flex-shrink-0">
                 Copyright © {currentYear}&nbsp;DayZ Exodus. All rights reserved. <br /> Created and designed by&nbsp;Captn FreshIce
            </footer>
        </div>
    </div>
  );
}

export default Layout;
